<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import mapboxgl from 'mapbox-gl'
import { bbox } from '@turf/turf'
import { Point, LineString, Polygon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, ZoomIn, FileText } from 'lucide-vue-next'

const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  feature: {
    type: Object,
    required: true
  },
  coordinates: {
    type: Array,
    required: true
  },
  projectId: {
    type: Number,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['close'])

const store = useStore()
const router = useRouter()
const popup = ref(null)
const popupInstance = ref(null)

const featureCode = computed(() => props.feature?.properties?.code)
const isVisible = computed(() => store.getters['geo/getFeatureVisibility'](featureCode.value))

const iconName = computed(() => {
  if (!props.feature?.geometry?.type) return 'point'
  const geometryType = props.feature.geometry.type.toLowerCase()
  const subtype = props.feature.properties?.subtype
  return subtype === 'report' || !subtype ? geometryType : subtype
})

const toggleVisibility = () => {
  store.commit('geo/TOGGLE_FEATURE_VISIBILITY', featureCode.value)
  closePopup()
}

const showInFeed = () => {
  closePopup()
  // Navigate to the feature page after a small delay to ensure popup closes first
  setTimeout(() => {
    router.push({
      name: 'Feature',
      params: {
        org_id: props.orgId,
        project_id: props.projectId,
        featureCode: featureCode.value
      }
    })
  }, 50)
}

const zoomToFeature = () => {
  if (!props.map || !props.feature) return

  try {
    const featureBbox = bbox(props.feature)
    props.map.fitBounds(featureBbox, {
      padding: 100,
      duration: 500
    })
  } catch (error) {
    console.error('Error zooming to feature:', error)
    // Fallback to center point if bbox fails
    if (props.coordinates) {
      props.map.flyTo({
        center: props.coordinates,
        zoom: 16,
        duration: 500
      })
    }
  }
  closePopup()
}

const closePopup = () => {
  if (popupInstance.value) {
    popupInstance.value.off('close') // Remove event listener to prevent recursion
    popupInstance.value.remove()
    popupInstance.value = null
  }
  emit('close')
}

const createPopup = () => {
  if (!props.map || !props.coordinates) return

  // Close any existing popup first
  if (popupInstance.value) {
    popupInstance.value.off('close')
    popupInstance.value.remove()
    popupInstance.value = null
  }

  // Wait for Vue to render the template
  nextTick(() => {
    if (!popup.value) return

    // Clone the Vue-rendered content
    const container = document.createElement('div')
    const clonedContent = popup.value.cloneNode(true)
    clonedContent.style.display = 'block' // Make it visible
    container.appendChild(clonedContent)

    popupInstance.value = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      maxWidth: '300px',
      className: 'feature-popup-container'
    })
      .setLngLat(props.coordinates)
      .setDOMContent(container.firstChild)
      .addTo(props.map)

    // Listen for popup close event
    popupInstance.value.on('close', () => {
      popupInstance.value = null
      emit('close')
    })

    // Attach event listeners after popup is added to DOM
    setTimeout(() => {
      const popupContent = popupInstance.value?.getElement()
      if (popupContent) {
        const visibilityBtn = popupContent.querySelector('[data-action="toggle-visibility"]')
        const feedBtn = popupContent.querySelector('[data-action="show-in-feed"]')
        const zoomBtn = popupContent.querySelector('[data-action="zoom"]')

        if (visibilityBtn) {
          visibilityBtn.addEventListener('click', (e) => {
            e.stopPropagation()
            toggleVisibility()
          })
        }
        if (feedBtn) {
          feedBtn.addEventListener('click', (e) => {
            e.stopPropagation()
            showInFeed()
          })
        }
        if (zoomBtn) {
          zoomBtn.addEventListener('click', (e) => {
            e.stopPropagation()
            zoomToFeature()
          })
        }
      }
    }, 100)
  })
}

// These functions are no longer needed as we're using Vue templates

onMounted(() => {
  createPopup()
})

onUnmounted(() => {
  closePopup()
})

watch(() => props.feature, () => {
  closePopup()
  setTimeout(createPopup, 50)
})
</script>

<template>
  <div ref="popup" v-show="false">
    <div class="p-2 min-w-[150px]">
      <!-- Feature Header -->
      <div class="flex items-center mb-3">
        <div
          class="min-w-8 min-h-8 w-8 h-8 rounded-full shadow-sm flex items-center justify-center mr-3"
          :style="{'background-color': props.feature.properties.color || '#059669'}"
        >
          <div v-if="props.feature.properties.s3_icon">
            <img class="object-contain w-5 h-5" :src="props.feature.properties.s3_icon" alt="" />
          </div>
          <div v-else>
            <Point v-if="iconName === 'point'" class="fill-white" />
            <LineString v-if="iconName === 'linestring'" class="fill-white" />
            <Polygon v-if="iconName === 'polygon' || iconName === 'multipolygon'" class="fill-white" />
          </div>
        </div>
        <div class="flex-1">
          <h3 class="font-semibold text-sm">{{ props.feature.properties.name || 'Unnamed Feature' }}</h3>
          <p class="text-xs text-gray-500">{{ props.feature.properties.member_display_name }}</p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div>
        <Button
          data-action="show-in-feed"
          variant="ghost"
          size="sm"
          class="w-full justify-start"
        >
          <FileText class="w-4 h-4 mr-2" />
          Open
        </Button>

        <Button
          data-action="toggle-visibility"
          :variant="isVisible ? 'ghost' : 'secondary'"
          size="sm"
          class="w-full justify-start"
        >
          <Eye v-if="isVisible" class="w-4 h-4 mr-2" />
          <EyeOff v-else class="w-4 h-4 mr-2" />
          {{ isVisible ? 'Hide' : 'Show' }}
        </Button>

        <Button
          data-action="zoom"
          variant="ghost"
          size="sm"
          class="w-full justify-start"
        >
          <ZoomIn class="w-4 h-4 mr-2" />
          Fit / Zoom
        </Button>
      </div>
    </div>
  </div>
</template>

<style>
.feature-popup-container .mapboxgl-popup-content {
  padding: 0;
  border-radius: 8px;
}

.feature-popup-container .mapboxgl-popup-close-button {
  font-size: 20px;
  padding: 4px 8px;
}
</style>
